// Core Wasm loader — dynamically imports a user-supplied core (coreURL), or
// falls back to @wasmedia/wffmpeg-core-st if it happens to be resolvable.
//
// That package is NOT published to npm: the build enables --enable-nonfree
// (libfdk-aac), and FFmpeg's own notice makes such a binary
// non-redistributable. It resolves only when the core has been built locally
// and linked in, e.g. inside this workspace.

import type { FFmpegConfig } from '@wasmedia/wffmpeg-types';
import type { CoreFactory, EmscriptenModule } from './module.js';
import { createModuleOverrides } from './module.js';
import { CoreSession, type InstanceHooks } from './session.js';
import { detectRuntime } from '../env/detect.js';

export interface LoadOptions {
  config: FFmpegConfig;
  onLog?: (type: 'ffout' | 'fferr', message: string) => void;
  onProgress?: (progress: number, time: number) => void;
  /** Exit / abort callbacks for this instance; see CoreSession. */
  hooks?: InstanceHooks;
}

async function resolveFactory(config: FFmpegConfig): Promise<CoreFactory> {
  if (config.coreURL) {
    const mod = await import(/* @vite-ignore */ config.coreURL);
    return mod.default ?? mod.createFFmpegCore ?? mod;
  }

  // new Function so bundlers don't try to resolve the optional core package.
  const dynamicImport = new Function('specifier', 'return import(specifier)') as (
    specifier: string,
  ) => Promise<Record<string, unknown>>;

  const packageName = '@wasmedia/wffmpeg-core-st';

  try {
    const mod = await dynamicImport(packageName);
    return (mod.default ?? mod.createFFmpegCore ?? mod) as CoreFactory;
  } catch {
    throw new Error(
      `No FFmpeg core found. ${packageName} is not published to npm because the ` +
        'build enables nonfree codecs, which FFmpeg does not permit redistributing. ' +
        'Build it with `pnpm build:wasm:docker` and pass FFmpegConfig.coreURL / ' +
        'wasmURL pointing at the output, or install the built package locally ' +
        `(npm install /path/to/packages/core-st).`,
    );
  }
}

/**
 * Creates one module instance. Its callMain() only starts main(); to run
 * commands, use createSession(), which waits for the real exit and gives each
 * run a fresh instance.
 */
export async function loadCore(options: LoadOptions): Promise<EmscriptenModule> {
  const { config, onLog, onProgress, hooks } = options;
  const runtime = detectRuntime();
  const overrides: Partial<EmscriptenModule> = {
    ...createModuleOverrides(onLog, onProgress),
    ...(hooks && { onExit: hooks.onExit, onAbort: hooks.onAbort }),
  };

  if (config.wasmURL) {
    (overrides as Record<string, unknown>).locateFile = (path: string) => {
      if (path.endsWith('.wasm')) return config.wasmURL!;
      return path;
    };
  }

  const factory = await resolveFactory(config);

  // Handle abort signal
  if (config.signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const modulePromise = factory(overrides);

  if (config.signal) {
    return Promise.race([
      modulePromise,
      new Promise<never>((_resolve, reject) => {
        config.signal!.addEventListener(
          'abort',
          () => reject(new DOMException('Aborted', 'AbortError')),
          { once: true },
        );
      }),
    ]);
  }

  const mod = await modulePromise;

  if (runtime === 'node') {
    // In Node.js, ensure the FS is ready
    // Emscripten typically calls onRuntimeInitialized, but with MODULARIZE
    // the returned promise already waits for that.
  }

  return mod;
}

/** Loads the core and returns a session that runs commands to completion. */
export function createSession(options: Omit<LoadOptions, 'hooks'>): Promise<CoreSession> {
  return CoreSession.create((hooks) => loadCore({ ...options, hooks }));
}
